import { type HttpClientConfig, type HttpClientInterface } from 'core/infra/http/HttpClient'

import { initialAccessorialRate, type AccessorialRate } from '../domain/Accessorial'
import {
  mapAccessorialRateToApi,
  mapApiToAccessorialRate,
  mapApiToAccessorialRateList,
  type RawAccessorialRate,
} from './AccessorialMapper'
import { CARRIER_INVITES_ROOT } from './createRfpGuideInviteInterceptor'

type AccessorialRepositoryDependencies = {
  httpClient: HttpClientInterface
}

export class AccessorialRepository {
  private httpClient: HttpClientInterface

  constructor({ httpClient }: AccessorialRepositoryDependencies) {
    this.httpClient = httpClient
  }

  async getAccessorialRates(carrierInvite: string, config?: Partial<HttpClientConfig>) {
    const endpoint = `${CARRIER_INVITES_ROOT}/${carrierInvite}/extra_charge`

    const { data = [] } = await this.httpClient.get<Array<RawAccessorialRate>>(endpoint, config)

    try {
      return mapApiToAccessorialRateList(data)
    } catch (error) {
      throw new Error('Failed to map API return to Accessorial Rate list', {
        cause: error as Error,
      })
    }
  }

  async createAccessorialRates(
    carrierInvite: string,
    accessorialRate: AccessorialRate,
    config?: Partial<HttpClientConfig>
  ) {
    const endpoint = `${CARRIER_INVITES_ROOT}/${carrierInvite}/extra_charge`
    const payload = mapAccessorialRateToApi(accessorialRate)
    const { data } = await this.httpClient.post<RawAccessorialRate>(endpoint, payload, config)

    try {
      return mapApiToAccessorialRate(data)
    } catch (error) {
      throw new Error('Failed to map API return to Accessorial Rate', { cause: error as Error })
    }
  }

  async updateAccessorialRates(
    carrierInvite: string,
    accessorialRate: AccessorialRate,
    config?: Partial<HttpClientConfig>
  ) {
    const endpoint = `${CARRIER_INVITES_ROOT}/${carrierInvite}/extra_charge/${accessorialRate.id}`
    const payload = mapAccessorialRateToApi(accessorialRate)
    const { data } = await this.httpClient.patch<RawAccessorialRate>(endpoint, payload, config)

    try {
      return mapApiToAccessorialRate(data)
    } catch (error) {
      throw new Error('Failed to map API return to Accessorial Rate', { cause: error as Error })
    }
  }

  async deleteAccessorialRates(
    carrierInvite: string,
    accessorialRate: AccessorialRate,
    config?: Partial<HttpClientConfig>
  ) {
    const endpoint = `${CARRIER_INVITES_ROOT}/${carrierInvite}/extra_charge/${accessorialRate.id}`

    await this.httpClient.delete(endpoint, config)

    return initialAccessorialRate
  }
}
